// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: names, lines and icons for Settings > Customise Home (design/home-mobile-compact round 2).
// The layout choices are one record keyed by HomeLayout, so a new layout in HomePreferences
// (Focus deck, Timeline) fails the typecheck here until it has a name, a line and a preview.

import type {MailSwipeTone} from '@app/features/konvo_mail/components/MailSwipeRow';
import type {
	HomeAllChatsGrouping,
	HomeDensity,
	HomeLayout,
	HomeOpenConversations,
	HomeSectionId,
	HomeSuggestionStyle,
	HomeSwipeAction,
	HomeSwipeDirection,
	HomeSwipeReach,
} from '@app/features/konvo_today/state/HomePreferences';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {
	BellIcon,
	ChartBarIcon,
	ChatCircleIcon,
	RowsIcon,
	SortAscendingIcon,
	SparkleIcon,
	StackSimpleIcon,
} from '@app/features/ui/icons/Icons';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {IconCards, IconTimeline} from '@tabler/icons-react';
import type React from 'react';

export const HOME_LAYOUT_LABEL: Record<HomeLayout, MessageDescriptor> = {
	stacks: msg({
		message: 'Stacks',
		comment: 'Customise Home layout choice: calm sections, the rest folded into stacks.',
	}),
	list: msg({message: 'List', comment: 'Customise Home layout choice: every item on Home as one swipe row.'}),
	deck: msg({
		message: 'Focus deck',
		comment: 'Customise Home layout choice: the to-dos one at a time in a card at the top of Home.',
	}),
	timeline: msg({
		message: 'Timeline',
		comment: "Customise Home layout choice: the to-dos placed in the free time between today's meetings.",
	}),
};

export const HOME_LAYOUT_DESCRIPTION: Record<HomeLayout, MessageDescriptor> = {
	stacks: msg({
		message: 'Top few open, the rest stacked',
		comment: 'One line under the Stacks layout card in Customise Home.',
	}),
	list: msg({message: 'Everything, one line each', comment: 'One line under the List layout card in Customise Home.'}),
	deck: msg({message: 'One thing at a time', comment: 'One line under the Focus deck layout card in Customise Home.'}),
	timeline: msg({
		message: 'To-dos between meetings',
		comment: 'One line under the Timeline layout card in Customise Home.',
	}),
};

export const HOME_LAYOUT_NOTE: Record<HomeLayout, MessageDescriptor> = {
	stacks: msg({
		message: 'Stacks keeps the first few things in each section open and stacks the rest. Tap a stack to open it.',
		comment: 'Note under the layout cards in Customise Home when Stacks is picked.',
	}),
	list: msg({
		message: 'List shows every thing as one row you can swipe, with the most important first.',
		comment: 'Note under the layout cards in Customise Home when List is picked.',
	}),
	deck: msg({
		message:
			'Focus deck puts your to-dos in one card at the top of Home, one at a time. Flick it right for Done, left for Later.',
		comment: 'Note under the layout cards in Customise Home when Focus deck is picked.',
	}),
	timeline: msg({
		message:
			'Timeline lays out the rest of today: your meetings, reminders at their time, and your to-dos in the free time between them.',
		comment: 'Note under the layout cards in Customise Home when Timeline is picked.',
	}),
};

export function homeLayoutIcon(layout: HomeLayout, size = 18): React.ReactNode {
	switch (layout) {
		case 'stacks':
			return <StackSimpleIcon size={size} />;
		case 'list':
			return <RowsIcon size={size} />;
		case 'deck':
			return <IconCards size={size} stroke={1.9} />;
		case 'timeline':
			return <IconTimeline size={size} stroke={1.9} />;
	}
}

export const HOME_DENSITY_LABEL: Record<HomeDensity, MessageDescriptor> = {
	comfortable: msg({message: 'Comfortable', comment: 'Customise Home density choice: roomy rows.'}),
	compact: msg({message: 'Compact', comment: 'Customise Home density choice: tighter rows, more on screen.'}),
};

export const HOME_SECTION_LABEL: Record<HomeSectionId, MessageDescriptor> = {
	today: msg({message: 'Things for today', comment: 'Home section name in Customise Home: the to-dos card.'}),
	konvo: msg({
		message: 'From Konvo',
		comment: "Home section name in Customise Home: the assistant's briefs and suggestions.",
	}),
	dashboards: msg({
		message: 'Dashboards',
		comment: 'Home section name in Customise Home: charts and dashboards the user pinned from the assistant.',
	}),
	sorted: msg({message: 'Sorted for you', comment: 'Home section name in Customise Home: the ranked feed.'}),
	all_chats: msg({
		message: 'All chats',
		comment: 'Home section name in Customise Home: every conversation, newest first.',
	}),
	coming_up: msg({message: 'Coming up', comment: 'Home section name in Customise Home: reminders and what is next.'}),
};

export const HOME_SECTION_DESCRIPTION: Record<HomeSectionId, MessageDescriptor> = {
	today: msg({
		message: 'Your to-dos, meetings and facts',
		comment: 'One line under "Things for today" in Customise Home > Sections.',
	}),
	konvo: msg({message: 'Briefs and suggestions', comment: 'One line under "From Konvo" in Customise Home > Sections.'}),
	dashboards: msg({
		message: 'Charts you pinned from Konvo',
		comment: 'One line under "Dashboards" in Customise Home > Sections.',
	}),
	sorted: msg({
		message: 'Ranked chats, mail and tasks',
		comment: 'One line under "Sorted for you" in Customise Home > Sections.',
	}),
	all_chats: msg({
		message: 'Every conversation, newest first',
		comment: 'One line under "All chats" in Customise Home > Sections.',
	}),
	coming_up: msg({
		message: "Reminders and what's next",
		comment: 'One line under "Coming up" in Customise Home > Sections.',
	}),
};

export function homeSectionIcon(id: HomeSectionId, size = 20): React.ReactNode {
	switch (id) {
		case 'today':
			return <SparkleIcon size={size} />;
		case 'konvo':
			return <KonvoLogo size={size} />;
		case 'dashboards':
			return <ChartBarIcon size={size} />;
		case 'sorted':
			return <SortAscendingIcon size={size} />;
		case 'all_chats':
			return <ChatCircleIcon size={size} />;
		case 'coming_up':
			return <BellIcon size={size} />;
	}
}

export const HOME_OPEN_CONVERSATIONS_LABEL: Record<HomeOpenConversations, MessageDescriptor> = {
	popup: msg({
		message: 'Pop-up',
		comment: 'Customise Home > Opening a conversation: it opens in a small card over Home.',
	}),
	side: msg({
		message: 'Side by side',
		comment: 'Customise Home > Opening a conversation: Home moves into a list and the conversation opens beside it.',
	}),
};

export const HOME_OPEN_CONVERSATIONS_DESCRIPTION: Record<HomeOpenConversations, MessageDescriptor> = {
	popup: msg({
		message: 'The last messages and a reply box in a card over Home. Expand opens it side by side.',
		comment: 'One line under the "Pop-up" choice in Customise Home > Opening a conversation.',
	}),
	side: msg({
		message: 'Home slides into a list on the left and the whole conversation opens beside it.',
		comment: 'One line under the "Side by side" choice in Customise Home > Opening a conversation.',
	}),
};

export const HOME_ALL_CHATS_GROUPING_LABEL: Record<HomeAllChatsGrouping, MessageDescriptor> = {
	day: msg({
		message: 'By day',
		comment: 'Customise Home > All chats: conversations grouped under Today, Yesterday and earlier days.',
	}),
	connection: msg({
		message: 'By connection',
		comment: 'Customise Home > All chats: conversations grouped by where they come from (Konvo, WhatsApp, mail...).',
	}),
};

export const HOME_ALL_CHATS_GROUPING_DESCRIPTION: Record<HomeAllChatsGrouping, MessageDescriptor> = {
	day: msg({
		message: 'Today, Yesterday, the days of this week, then Earlier. Every connection mixed together.',
		comment: 'One line under the "By day" choice in Customise Home > All chats.',
	}),
	connection: msg({
		message: "A group for each connection, in the source rail's order: Konvo, your servers, then the rest.",
		comment: 'One line under the "By connection" choice in Customise Home > All chats.',
	}),
};

/** The line under "All chats" in Customise Home > Sections, which follows how it is grouped. */
export const HOME_ALL_CHATS_BY_CONNECTION_DESCRIPTION = msg({
	message: 'Every conversation, by connection',
	comment: 'One line under "All chats" in Customise Home > Sections when it is grouped by connection.',
});

export const HOME_SUGGESTION_STYLE_LABEL: Record<HomeSuggestionStyle, MessageDescriptor> = {
	stack: msg({message: 'Stack', comment: "Customise Home: Konvo's suggestions shown as one stack of cards."}),
	list: msg({message: 'List', comment: "Customise Home: Konvo's suggestions shown as a list of rows."}),
};

export const HOME_SWIPE_DIRECTION_LABEL: Record<HomeSwipeDirection, MessageDescriptor> = {
	right: msg({
		message: 'Swipe right',
		comment: 'Customise Home > Swipe actions: the row for swiping a Home row right.',
	}),
	left: msg({message: 'Swipe left', comment: 'Customise Home > Swipe actions: the row for swiping a Home row left.'}),
};

export const HOME_SWIPE_REACH_LABEL: Record<HomeSwipeReach, MessageDescriptor> = {
	short: msg({message: 'Short', comment: 'Customise Home > Swipe actions: a short swipe (past the first line).'}),
	long: msg({message: 'Long', comment: 'Customise Home > Swipe actions: a long swipe (past the second line).'}),
};

/** Home actions drawn in the phone mail swipe colours (MailSwipeRow's tones). */
export const HOME_SWIPE_MAIL_TONE: Record<Exclude<HomeSwipeAction, 'none'>, MailSwipeTone> = {
	done: 'archive',
	reply: 'read',
	later: 'remind',
	dismiss: 'move',
	task: 'task',
	ask: 'reply',
};
